// Input validation for error reporting system
import { CreateErrorLogInput } from '../../types/error-logs';

/**
 * Validates error log input data
 * @param input Error log input data
 * @returns Validation result with errors if any
 */
export function validateErrorLogInput(input: Partial<CreateErrorLogInput>): { 
  isValid: boolean; 
  errors: Record<string, string>;
  sanitizedInput?: CreateErrorLogInput;
} {
  const errors: Record<string, string> = {};
  
  // Required fields
  if (!input.error_message) {
    errors.error_message = 'Error message is required';
  } else if (typeof input.error_message !== 'string') {
    errors.error_message = 'Error message must be a string';
  } else if (input.error_message.length > 10000) {
    errors.error_message = 'Error message is too long (max 10000 characters)';
  }
  
  // URL validation
  if (input.url) {
    try {
      // Check if URL is valid
      new URL(input.url);
    } catch (e) {
      errors.url = 'Invalid URL format';
    }
  } else {
    errors.url = 'URL is required';
  }
  
  // Validate error_type
  if (input.error_type) {
    if (typeof input.error_type !== 'string') {
      errors.error_type = 'Error type must be a string';
    } else if (input.error_type.length > 255) {
      errors.error_type = 'Error type is too long (max 255 characters)';
    }
  } else {
    errors.error_type = 'Error type is required';
  }
  
  // Validate component
  if (input.component && typeof input.component !== 'string') {
    errors.component = 'Component must be a string';
  } else if (input.component && input.component.length > 255) {
    errors.component = 'Component is too long (max 255 characters)';
  }
  
  // Validate browser_info
  if (!input.browser_info || typeof input.browser_info !== 'object') {
    errors.browser_info = 'Browser info is required and must be an object';
  }
  
  // Validate device_info
  if (!input.device_info || typeof input.device_info !== 'object') {
    errors.device_info = 'Device info is required and must be an object';
  }
  
  // Validate app_version
  if (!input.app_version) {
    errors.app_version = 'App version is required';
  } else if (typeof input.app_version !== 'string') {
    errors.app_version = 'App version must be a string';
  } else if (input.app_version.length > 100) {
    errors.app_version = 'App version is too long (max 100 characters)';
  }
  
  // Check if there are any errors
  const isValid = Object.keys(errors).length === 0;
  
  // If valid, create sanitized input
  let sanitizedInput: CreateErrorLogInput | undefined;
  
  if (isValid && input.error_message && input.url && input.error_type && 
      input.browser_info && input.device_info && input.app_version) {
    // Create sanitized input with all required fields
    sanitizedInput = {
      error_message: input.error_message.slice(0, 10000),
      error_stack: input.error_stack ? input.error_stack.slice(0, 10000) : null,
      error_type: input.error_type.slice(0, 255),
      component: input.component ? input.component.slice(0, 255) : null,
      url: input.url,
      route: input.route ? input.route.slice(0, 255) : null,
      browser_info: input.browser_info,
      device_info: input.device_info,
      app_version: input.app_version.slice(0, 100),
      user_id: input.user_id || null,
      additional_context: input.additional_context || null
    };
  }
  
  return { isValid, errors, sanitizedInput };
}

/**
 * Validates error ID
 * @param id Error ID to validate
 * @returns Whether the ID is valid
 */
export function validateErrorId(id: string): boolean {
  // UUID validation regex
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
}

/**
 * Validates search query parameters
 * @param query Search query parameters
 * @returns Sanitized query parameters
 */
export function validateSearchParams(query: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {};
  
  // Validate page number
  if (query.page) {
    const page = parseInt(query.page);
    sanitized.page = !isNaN(page) && page > 0 ? page : 1;
  }
  
  // Validate limit
  if (query.limit) {
    const limit = parseInt(query.limit);
    sanitized.limit = !isNaN(limit) && limit > 0 && limit <= 100 ? limit : 20;
  }
  
  // Validate search query
  if (query.searchQuery && typeof query.searchQuery === 'string') {
    sanitized.searchQuery = query.searchQuery.slice(0, 100);
  }
  
  // Validate dates
  if (query.fromDate && typeof query.fromDate === 'string') {
    // Simple date validation
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateRegex.test(query.fromDate)) {
      sanitized.fromDate = query.fromDate;
    }
  }
  
  if (query.toDate && typeof query.toDate === 'string') {
    // Simple date validation
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateRegex.test(query.toDate)) {
      sanitized.toDate = query.toDate;
    }
  }
  
  // Validate sort parameters
  if (query.sortBy && typeof query.sortBy === 'string') {
    // Only allow specific sort fields
    const allowedSortFields = ['created_at', 'error_type', 'status', 'component'];
    sanitized.sortBy = allowedSortFields.includes(query.sortBy) ? query.sortBy : 'created_at';
  }
  
  if (query.sortOrder && typeof query.sortOrder === 'string') {
    sanitized.sortOrder = query.sortOrder === 'asc' ? 'asc' : 'desc';
  }
  
  return sanitized;
}
